import {
    Children,
    cloneElement,
    createContext,
    isValidElement,
    useContext,
    useLayoutEffect,
    useEffect,
    useId,
    useRef,
    useState,
    type ChangeEvent,
    type ElementType,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import { X } from 'lucide-react';
import { AppModal, DataTable, ErrorState, InlineStyleBox, LoadingState, StatusBadge } from './core';
import { computeTooltipPosition, type TooltipPosition } from './tooltipPosition';
import {
    cx,
    renderAdornment,
    responsiveDirection,
    responsiveSpan,
    splitStyleProps,
    sxToStyle,
    toneToAlertClass,
    toneToBadgeClass,
    toneToButtonClass,
} from './utils';
import { useSharedUiText } from './sharedUiText';

export type AlertColor = 'success' | 'info' | 'warning' | 'error';
export type SelectChangeEvent<T = string> = ChangeEvent<HTMLSelectElement> & {
    target: EventTarget & HTMLSelectElement & { value: T };
};

const SelectContext = createContext(false);
const TabsContext = createContext<{
    name: string;
    value: unknown;
    onChange?: (event: unknown, value: unknown) => void;
} | null>(null);
const ToggleContext = createContext<{
    value: unknown;
    exclusive?: boolean;
    onChange?: (event: unknown, value: unknown) => void;
} | null>(null);
const RadioContext = createContext<{
    value: unknown;
    name?: string;
    onChange?: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
} | null>(null);
const StepperContext = createContext<{ activeStep: number }>({ activeStep: 0 });
/** Tooltip 的文字同時是圖示按鈕唯一的名稱，讓螢幕閱讀器念得出來 */
const TooltipTitleContext = createContext<string | null>(null);
const DialogTitleContext = createContext<string | undefined>(undefined);
const TableContainerContext = createContext(false);
/** MUI 的 Button 預設是 text；對話框按鈕列裡沒指定樣式的按鈕（例如「取消」）不該和主要動作一樣醒目 */
const DialogActionsContext = createContext(false);

function asComponent(component: any, fallback: any) {
    return component || fallback;
}

function mapComponentProps(rest: Record<string, any>) {
    if (rest.to && !rest.href) {
        return { ...rest, href: undefined };
    }

    return rest;
}

function resolveMaxWidth(maxWidth?: string | false) {
    switch (maxWidth) {
        case 'xs':
            return 'max-w-xs';
        case 'sm':
            return 'max-w-sm';
        case 'md':
            return 'max-w-2xl';
        case 'lg':
            return 'max-w-4xl';
        case 'xl':
            return 'max-w-6xl';
        case false:
            return 'max-w-none';
        default:
            return 'max-w-7xl';
    }
}

export function Box(props: any) {
    return <InlineStyleBox {...props} component={asComponent(props.component, 'div')} />;
}

export function Container(props: any) {
    const { className, children, sxProp, maxWidth = 'lg', ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div
            className={cx(
                'mx-auto w-full px-5 sm:px-8 lg:px-10',
                resolveMaxWidth(maxWidth),
                className
            )}
            style={style}
            {...domProps}
        >
            {children}
        </div>
    );
}

const TYPOGRAPHY_COLOR_CLASS: Record<string, string> = {
    'text.secondary': 'text-base-content/70',
    'text.disabled': 'text-base-content/60',
    error: 'text-error',
    'error.main': 'text-error',
    primary: 'text-primary',
    'primary.main': 'text-primary',
    success: 'text-success',
    'success.main': 'text-success',
    warning: 'text-warning',
    'warning.main': 'text-warning',
    info: 'text-info',
    'info.main': 'text-info',
};

const TYPOGRAPHY_MAP: Record<string, { tag: ElementType; className: string }> = {
    h3: { tag: 'h3', className: 'text-4xl font-semibold' },
    h4: { tag: 'h1', className: 'text-3xl font-semibold' },
    h5: { tag: 'h2', className: 'text-2xl font-semibold' },
    h6: { tag: 'h3', className: 'text-xl font-semibold' },
    subtitle1: { tag: 'h4', className: 'text-lg font-medium' },
    subtitle2: { tag: 'h5', className: 'text-sm font-semibold' },
    body2: { tag: 'p', className: 'text-sm' },
    caption: { tag: 'span', className: 'text-xs text-base-content/60' },
};

export function Typography(props: any) {
    const {
        variant = 'body1',
        gutterBottom,
        color,
        align,
        className,
        sxProp,
        component,
        noWrap,
        paragraph,
        ...rest
    } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    const config = TYPOGRAPHY_MAP[variant] || { tag: 'p' as const, className: 'text-base' };
    const Component = asComponent(component, config.tag);
    return (
        <Component
            className={cx(
                config.className,
                gutterBottom ? 'mb-2' : '',
                paragraph ? 'mb-4' : '',
                noWrap ? 'truncate' : '',
                align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : '',
                TYPOGRAPHY_COLOR_CLASS[color] ?? '',
                className
            )}
            style={style}
            {...domProps}
        />
    );
}

export function Button(props: any) {
    const inDialogActions = useContext(DialogActionsContext);
    const {
        children,
        variant = inDialogActions ? 'text' : 'contained',
        color = 'primary',
        startIcon,
        endIcon,
        fullWidth,
        size = 'medium',
        className,
        component,
        sxProp,
        ...rest
    } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    const Component = component || (rest.to ? RouterLink : rest.href ? 'a' : 'button');
    const baseClass =
        variant === 'text' ? 'btn btn-ghost' : variant === 'outlined' ? 'btn btn-outline' : 'btn';
    return (
        <Component
            className={cx(
                baseClass,
                toneToButtonClass(color),
                size === 'small' ? 'btn-sm' : size === 'large' ? 'btn-lg' : '',
                fullWidth ? 'w-full' : '',
                className
            )}
            style={style}
            {...mapComponentProps(domProps)}
        >
            {startIcon ? <span className="inline-flex items-center">{startIcon}</span> : null}
            {children}
            {endIcon ? <span className="inline-flex items-center">{endIcon}</span> : null}
        </Component>
    );
}

export function IconButton(props: any) {
    const { children, className, color = 'inherit', size = 'medium', sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    const tooltipTitle = useContext(TooltipTitleContext);
    const t = useSharedUiText();
    const iconNameKey = (
        Children.toArray(children).find(isValidElement)?.type as { accessibleNameKey?: string }
    )?.accessibleNameKey;
    return (
        <button
            type="button"
            aria-label={tooltipTitle ?? (iconNameKey ? t(iconNameKey) : undefined)}
            className={cx(
                'btn btn-ghost btn-circle',
                color === 'primary' ? 'text-primary' : '',
                size === 'small' ? 'btn-sm' : '',
                className
            )}
            style={style}
            {...domProps}
        >
            {children}
        </button>
    );
}

export function Fab(props: any) {
    const { children, color = 'primary', className, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <button
            type="button"
            className={cx('btn btn-circle shadow-lg', toneToButtonClass(color), className)}
            style={style}
            {...domProps}
        >
            {children}
        </button>
    );
}

export function Alert(props: any) {
    const { children, severity = 'info', className, sxProp, icon, onClose, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    const t = useSharedUiText();
    return (
        <div
            role={severity === 'error' || severity === 'warning' ? 'alert' : 'status'}
            className={cx('alert', toneToAlertClass(severity), className)}
            style={style}
            {...domProps}
        >
            {icon !== false ? icon || null : null}
            <span>{children}</span>
            {onClose ? (
                <button
                    type="button"
                    className="btn btn-ghost btn-circle btn-sm"
                    aria-label={t('close')}
                    onClick={onClose}
                >
                    <X className="h-4 w-4" />
                </button>
            ) : null}
        </div>
    );
}

export function Snackbar(props: any) {
    const { open, children, anchorOrigin, onClose, autoHideDuration } = props;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!open || !autoHideDuration) return undefined;
        const timer = window.setTimeout(
            () => onCloseRef.current?.(undefined, 'timeout'),
            autoHideDuration
        );
        return () => window.clearTimeout(timer);
    }, [open, autoHideDuration]);

    if (!open) return null;
    const placement =
        anchorOrigin?.vertical === 'top'
            ? anchorOrigin?.horizontal === 'center'
                ? 'top-4 left-1/2 -translate-x-1/2'
                : anchorOrigin?.horizontal === 'right'
                  ? 'top-4 right-4'
                  : 'top-4 left-4'
            : anchorOrigin?.horizontal === 'center'
              ? 'bottom-4 left-1/2 -translate-x-1/2'
              : anchorOrigin?.horizontal === 'right'
                ? 'bottom-4 right-4'
                : 'bottom-4 left-4';
    return createPortal(
        <div
            className={cx(
                'fixed z-[120] w-max max-w-[calc(100vw-2rem)] sm:max-w-lg [&_.alert]:shadow-lg',
                placement
            )}
            onClick={() => onClose?.()}
        >
            {children}
        </div>,
        document.body
    );
}

export function Paper(props: any) {
    const { children, className, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div className={cx('card bg-base-100 shadow-sm', className)} style={style} {...domProps}>
            {children}
        </div>
    );
}

export function Card(props: any) {
    return <Paper {...props} />;
}

export function CardContent(props: any) {
    const { className, children, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div className={cx('card-body', className)} style={style} {...domProps}>
            {children}
        </div>
    );
}

export function CardHeader(props: any) {
    const { avatar, action, title, subheader, className } = props;
    return (
        <div className={cx('flex items-start justify-between gap-4 p-6 pb-0', className)}>
            <div className="flex items-start gap-3">
                {avatar}
                <div>
                    <div className="text-lg font-semibold">{title}</div>
                    {subheader ? (
                        <div className="text-sm text-base-content/60">{subheader}</div>
                    ) : null}
                </div>
            </div>
            {action}
        </div>
    );
}

export function CardActionArea(props: any) {
    const { children, onClick, className, sxProp, ...rest } = props;
    return (
        <button
            type="button"
            className={cx('w-full text-left transition hover:bg-base-200/60', className)}
            onClick={onClick}
            style={sxToStyle(sxProp)}
            {...rest}
        >
            {children}
        </button>
    );
}

export function Divider(props: any) {
    const { className, sxProp, ...rest } = props;
    return <div className={cx('divider my-0', className)} style={sxToStyle(sxProp)} {...rest} />;
}

export function CircularProgress(props: any) {
    const { size = 'loading-lg', className } = props;
    return <span className={cx('loading loading-spinner text-primary', size, className)} />;
}

export function LinearProgress(props: any) {
    const { value, className, sxProp } = props;
    return (
        <progress
            className={cx('progress progress-primary w-full', className)}
            value={value}
            max={value === undefined ? undefined : 100}
            style={sxToStyle(sxProp)}
        />
    );
}

export function Skeleton(props: any) {
    const { variant, height, width, className, sxProp } = props;
    return (
        <div
            className={cx('skeleton', variant === 'text' ? 'h-4' : '', className)}
            style={{ height, width, ...sxToStyle(sxProp) }}
        />
    );
}

export function Avatar(props: any) {
    const { children, src, alt, className, sxProp, ...rest } = props;
    const style = sxToStyle(sxProp);
    const size = style.width || style.height || 40;
    return (
        <div className={cx('avatar placeholder', className)} {...rest}>
            <div
                className="flex items-center justify-center rounded-full bg-primary/15 text-primary"
                style={{ width: size, height: size, ...style }}
            >
                {src ? <img src={src} alt={alt} className="rounded-full object-cover" /> : children}
            </div>
        </div>
    );
}

export function Chip(props: any) {
    const {
        label,
        children,
        color = 'default',
        variant,
        className,
        sxProp,
        icon,
        onDelete,
        ...rest
    } = props;
    const t = useSharedUiText();
    return (
        <span
            className={cx(
                'badge badge-md inline-flex shrink-0 items-center gap-1 whitespace-nowrap',
                toneToBadgeClass(color),
                variant === 'outlined' ? 'badge-outline' : '',
                className
            )}
            style={sxToStyle(sxProp)}
            {...rest}
        >
            {icon ? <span className="shrink-0">{icon}</span> : null}
            <span className="whitespace-nowrap">{label || children}</span>
            {onDelete ? (
                <button
                    type="button"
                    className="-mr-1 inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-base-content/15"
                    aria-label={t('close')}
                    onClick={onDelete}
                >
                    <X className="h-3 w-3" />
                </button>
            ) : null}
        </span>
    );
}

export function Tooltip(props: any) {
    const { title, children } = props;
    const text = typeof title === 'string' ? title : '';
    const anchorRef = useRef<HTMLSpanElement>(null);
    const bubbleRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<TooltipPosition | null>(null);

    const hideTooltip = () => {
        setIsOpen(false);
        setPosition(null);
    };

    // 先在原地渲染一次量出提示的大小，再換算成視窗座標
    useLayoutEffect(() => {
        if (!isOpen || !anchorRef.current || !bubbleRef.current) return;
        setPosition(
            computeTooltipPosition(
                anchorRef.current.getBoundingClientRect(),
                bubbleRef.current.getBoundingClientRect(),
                window.innerWidth
            )
        );
    }, [isOpen, text]);

    useEffect(() => {
        if (!isOpen) return;
        const hideOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') hideTooltip();
        };
        window.addEventListener('keydown', hideOnEscape);
        // 捲動或縮放後元件位置已變，直接收起，比跟著重算單純
        window.addEventListener('scroll', hideTooltip, true);
        window.addEventListener('resize', hideTooltip);
        return () => {
            window.removeEventListener('keydown', hideOnEscape);
            window.removeEventListener('scroll', hideTooltip, true);
            window.removeEventListener('resize', hideTooltip);
        };
    }, [isOpen]);

    return (
        <TooltipTitleContext.Provider value={text || null}>
            <span
                ref={anchorRef}
                className="tooltip-anchor"
                onMouseEnter={() => text && setIsOpen(true)}
                onMouseLeave={hideTooltip}
                onFocus={() => text && setIsOpen(true)}
                onBlur={hideTooltip}
            >
                {children}
            </span>
            {isOpen && text
                ? createPortal(
                      <div
                          ref={bubbleRef}
                          role="tooltip"
                          className="pointer-events-none fixed z-[1000] max-w-xs rounded-md bg-neutral px-2 py-1 text-xs text-neutral-content shadow-md"
                          style={{
                              left: position?.left ?? 0,
                              top: position?.top ?? 0,
                              visibility: position ? 'visible' : 'hidden',
                          }}
                      >
                          {text}
                      </div>,
                      document.body
                  )
                : null}
        </TooltipTitleContext.Provider>
    );
}

export function AppBar(props: any) {
    const { children, position = 'sticky', className, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div
            className={cx(
                'navbar border-b border-base-300 bg-base-100/90 px-5 backdrop-blur md:px-8',
                position === 'fixed' ? 'fixed inset-x-0 top-0 z-30' : 'sticky top-0 z-30',
                className
            )}
            style={style}
            {...domProps}
        >
            {children}
        </div>
    );
}

export function Toolbar(props: any) {
    const { children, className, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div
            className={cx('flex min-h-16 w-full items-center gap-4', className)}
            style={style}
            {...domProps}
        >
            {children}
        </div>
    );
}

export function Drawer(props: any) {
    const { children, open = true, variant = 'temporary', className, sxProp } = props;
    if (variant === 'permanent') {
        return (
            <aside
                className={cx('w-60 border-r border-base-300 bg-base-100', className)}
                style={sxToStyle(sxProp)}
            >
                {children}
            </aside>
        );
    }

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex">
            <button type="button" className="flex-1 bg-black/40" onClick={props.onClose} />
            <aside
                className={cx(
                    'h-full w-72 overflow-y-auto border-l border-base-300 bg-base-100',
                    className
                )}
                style={sxToStyle(sxProp)}
            >
                {children}
            </aside>
        </div>,
        document.body
    );
}

export function List(props: any) {
    const { children, className, subheader, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div className={cx('menu gap-2 p-3', className)} style={style} {...domProps}>
            {subheader}
            {children}
        </div>
    );
}

export function ListSubheader(props: any) {
    const { children, className, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div
            className={cx('menu-title px-3 py-2 text-xs text-base-content/60', className)}
            style={style}
            {...domProps}
        >
            {children}
        </div>
    );
}

export function ListItem(props: any) {
    const { children, className, sxProp, disablePadding, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div className={cx(disablePadding ? '' : 'px-1', className)} style={style} {...domProps}>
            {children}
        </div>
    );
}

export function ListItemButton(props: any) {
    const {
        children,
        selected,
        className,
        component,
        sxProp,
        disablePadding,
        dense,
        divider,
        alignItems,
        ...rest
    } = props;
    const Component = component || (rest.to ? RouterLink : 'button');
    return (
        <Component
            className={cx(
                'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition hover:bg-base-200',
                selected ? 'bg-primary text-primary-content hover:bg-primary/90' : '',
                className
            )}
            style={sxToStyle(sxProp)}
            {...mapComponentProps(rest)}
        >
            {children}
        </Component>
    );
}

export function ListItemIcon(props: any) {
    const { children, className, sxProp, ...rest } = props;
    return (
        <span
            className={cx('inline-flex h-5 w-5 shrink-0 items-center justify-center', className)}
            style={sxToStyle(sxProp)}
            {...rest}
        >
            {children}
        </span>
    );
}

export function ListItemText(props: any) {
    const { primary, secondary, className } = props;
    return (
        <span className={cx('flex min-w-0 flex-1 flex-col', className)}>
            <span className="truncate">{primary}</span>
            {secondary ? <span className="text-xs opacity-95">{secondary}</span> : null}
        </span>
    );
}

export function ListItemSecondaryAction(props: any) {
    return <span className="ml-auto flex items-center gap-2">{props.children}</span>;
}

export function Collapse(props: any) {
    const { in: open, children, unmountOnExit, className, sxProp } = props;
    if (!open && unmountOnExit) return null;
    return (
        <div
            className={cx(className)}
            style={{ display: open ? undefined : 'none', ...sxToStyle(sxProp) }}
        >
            {children}
        </div>
    );
}

export function Link(props: any) {
    const { className, component, ...rest } = props;
    const Component = component || (rest.to ? RouterLink : 'a');
    return <Component className={cx('link link-hover', className)} {...mapComponentProps(rest)} />;
}

export function InputLabel(props: any) {
    return <span className={props.className}>{props.children}</span>;
}

export function FormLabel(props: any) {
    return (
        <label className={cx('mb-2 block text-sm font-medium', props.className)}>
            {props.children}
        </label>
    );
}

export function FormControl(props: any) {
    const { children, fullWidth, className, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <label
            className={cx('floating-label', fullWidth ? 'w-full [&>select]:w-full' : '', className)}
            style={style}
            {...domProps}
        >
            {children}
        </label>
    );
}

const MENU_VIEWPORT_MARGIN_PX = 8;
const MENU_ANCHOR_GAP_PX = 8;

/** 把選單夾在視窗內：右側放不下就往左靠，下方放不下就改開在錨點上方 */
function computeMenuPosition(anchor: HTMLElement, menu: HTMLElement) {
    const anchorRect = anchor.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const maxLeft = window.innerWidth - menuRect.width - MENU_VIEWPORT_MARGIN_PX;
    const left = Math.max(Math.min(anchorRect.left, maxLeft), MENU_VIEWPORT_MARGIN_PX);
    const fitsBelow =
        anchorRect.bottom + MENU_ANCHOR_GAP_PX + menuRect.height <=
        window.innerHeight - MENU_VIEWPORT_MARGIN_PX;
    const top = fitsBelow
        ? anchorRect.bottom + MENU_ANCHOR_GAP_PX
        : Math.max(anchorRect.top - MENU_ANCHOR_GAP_PX - menuRect.height, MENU_VIEWPORT_MARGIN_PX);
    return { top, left };
}

function MenuSurface({ anchorEl, children, onClose }: any) {
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    useLayoutEffect(() => {
        if (menuRef.current) setPosition(computeMenuPosition(anchorEl, menuRef.current));
    }, [anchorEl, children]);

    // 定位完成前選單是 visibility: hidden，瀏覽器不會讓隱藏的元素取得焦點，所以要等到可見後才聚焦第一項
    const isPositioned = position !== null;
    useEffect(() => {
        if (!isPositioned) return;
        menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')?.focus();
    }, [isPositioned]);

    useEffect(() => {
        const menu = menuRef.current;
        const items = () =>
            Array.from(
                menu?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? []
            );
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose?.();
                return;
            }
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            event.preventDefault();
            const focusable = items();
            const current = focusable.indexOf(document.activeElement as HTMLElement);
            const step = event.key === 'ArrowDown' ? 1 : -1;
            focusable[(current + step + focusable.length) % focusable.length]?.focus();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            (anchorEl as HTMLElement).focus?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return createPortal(
        <div className="fixed inset-0 z-[90]" onClick={() => onClose?.()}>
            <div
                ref={menuRef}
                role="menu"
                className="absolute min-w-52 rounded-2xl border border-base-300 bg-base-100 p-2 shadow-xl"
                style={{
                    top: position?.top ?? 0,
                    left: position?.left ?? 0,
                    visibility: position ? 'visible' : 'hidden',
                }}
                onClick={(event) => event.stopPropagation()}
            >
                {children}
            </div>
        </div>,
        document.body
    );
}

export function Menu(props: any) {
    const { anchorEl, open, children, onClose } = props;
    if (!open || !anchorEl) return null;
    return (
        <MenuSurface anchorEl={anchorEl} onClose={onClose}>
            {children}
        </MenuSurface>
    );
}

export function MenuItem(props: any) {
    const isSelect = useContext(SelectContext);
    const { children, className, selected, sxProp, dense, divider, ...rest } = props;
    if (isSelect) {
        return <option {...rest}>{children}</option>;
    }

    return (
        <button
            type="button"
            role="menuitem"
            className={cx(
                'btn btn-ghost flex w-full justify-start',
                selected ? 'bg-primary/10 text-primary' : '',
                className
            )}
            style={sxToStyle(sxProp)}
            {...rest}
        >
            {children}
        </button>
    );
}

export function TextField(props: any) {
    const {
        label,
        helperText,
        error,
        select,
        multiline,
        rows = 4,
        fullWidth,
        className,
        sxProp,
        InputProps,
        inputProps,
        slotProps,
        InputLabelProps,
        SelectProps,
        FormHelperTextProps,
        minRows,
        maxRows,
        size,
        variant,
        margin,
        color,
        focused,
        type = 'text',
        children,
        ...rest
    } = props;

    const widthCls = fullWidth ? 'w-full' : '';
    const helperId = useId();
    const describedBy = helperText ? helperId : undefined;
    const controlProps = {
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
    };

    const inputElement = select ? (
        <SelectContext.Provider value>
            <select
                className={cx('select', widthCls, error ? 'select-error' : '')}
                {...controlProps}
                {...rest}
            >
                {children}
            </select>
        </SelectContext.Provider>
    ) : multiline ? (
        <textarea
            className={cx('textarea', widthCls, error ? 'textarea-error' : '')}
            rows={minRows ?? rows}
            {...controlProps}
            {...rest}
        />
    ) : (
        <div className={cx('input', widthCls, error ? 'input-error' : '')}>
            {renderAdornment(InputProps?.startAdornment)}
            <input type={type} {...controlProps} {...inputProps} {...rest} />
            {renderAdornment(InputProps?.endAdornment)}
        </div>
    );

    return (
        <div className={cx(widthCls, className)} style={sxToStyle(sxProp)}>
            <label className={cx('floating-label', widthCls)}>
                {inputElement}
                {label ? (
                    <span>
                        {label}
                        {rest.required ? (
                            <span aria-hidden="true" className="text-error">
                                {' *'}
                            </span>
                        ) : null}
                    </span>
                ) : null}
            </label>
            {helperText ? (
                <p id={helperId} className={cx('mt-1', error ? 'field-error' : 'field-hint')}>
                    {helperText}
                </p>
            ) : null}
        </div>
    );
}

export function Select(props: any) {
    const { children, fullWidth, className, sxProp, ...rest } = props;
    return (
        <SelectContext.Provider value>
            <select
                className={cx('select', fullWidth ? 'w-full' : '', className)}
                style={sxToStyle(sxProp)}
                {...rest}
            >
                {children}
            </select>
        </SelectContext.Provider>
    );
}

export function InputAdornment(props: any) {
    return <span className="inline-flex items-center">{props.children}</span>;
}

export function Checkbox(props: any) {
    const { className, sxProp, indeterminate, ...rest } = props;
    const checkboxRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        if (checkboxRef.current) checkboxRef.current.indeterminate = Boolean(indeterminate);
    }, [indeterminate]);
    return (
        <input
            ref={checkboxRef}
            type="checkbox"
            className={cx('checkbox checkbox-primary', className)}
            style={sxToStyle(sxProp)}
            {...rest}
        />
    );
}

export function Switch(props: any) {
    const { className, sxProp, ...rest } = props;
    return (
        <input
            type="checkbox"
            className={cx('toggle toggle-primary', className)}
            style={sxToStyle(sxProp)}
            {...rest}
        />
    );
}

export function Radio(props: any) {
    const context = useContext(RadioContext);
    const { value, className, ...rest } = props;
    return (
        <input
            type="radio"
            className={cx('radio radio-primary', className)}
            checked={context?.value === value}
            name={context?.name}
            onChange={(event) => context?.onChange?.(event, value)}
            value={value}
            {...rest}
        />
    );
}

export function FormControlLabel(props: any) {
    const { control, label, className, value, checked, onChange, disabled, name } = props;
    // MUI 的 FormControlLabel 會把 value／checked／onChange／disabled／name 交給裡面的控制項
    const forwarded = Object.fromEntries(
        Object.entries({ value, checked, onChange, disabled, name }).filter(
            ([key, forwardedValue]) =>
                forwardedValue !== undefined &&
                isValidElement(control) &&
                !(key in (control.props as object))
        )
    );
    const controlElement = isValidElement(control) ? cloneElement(control, forwarded) : control;
    return (
        <label className={cx('flex min-h-10 cursor-pointer items-center gap-3 text-sm', className)}>
            {controlElement}
            <span>{label}</span>
        </label>
    );
}

export function FormGroup(props: any) {
    const { row, className, children, sxProp, ...rest } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div
            className={cx(
                'flex gap-3',
                row ? 'flex-row flex-wrap items-center' : 'flex-col',
                className
            )}
            style={style}
            {...domProps}
        >
            {children}
        </div>
    );
}

export function RadioGroup(props: any) {
    const { value, name, onChange, row, children, className } = props;
    return (
        <RadioContext.Provider value={{ value, name, onChange }}>
            <div
                className={cx(
                    'flex gap-3',
                    row ? 'flex-row flex-wrap items-center' : 'flex-col',
                    className
                )}
            >
                {children}
            </div>
        </RadioContext.Provider>
    );
}

export function Grid(props: any) {
    const {
        container,
        item,
        spacing = 2,
        size,
        xs,
        sm,
        md,
        lg,
        xl,
        className,
        sxProp,
        children,
        ...rest
    } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    if (container) {
        return (
            <div
                className={cx('grid grid-cols-12', className)}
                style={{ gap: `${spacing * 8}px`, ...style }}
                {...domProps}
            >
                {children}
            </div>
        );
    }

    const spanValue = size || { xs, sm, md, lg, xl };
    return (
        <div className={cx(responsiveSpan(spanValue), className)} style={style} {...domProps}>
            {children}
        </div>
    );
}

export function Stack(props: any) {
    const {
        children,
        direction = 'column',
        spacing = 1,
        className,
        sxProp,
        useFlexGap,
        ...rest
    } = props;
    void useFlexGap;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    return (
        <div
            className={cx('flex', responsiveDirection(direction, 'column'), className)}
            style={{ gap: `${spacing * 8}px`, ...style }}
            {...domProps}
        >
            {children}
        </div>
    );
}

export function TableContainer(props: any) {
    return (
        <TableContainerContext.Provider value>
            <DataTable {...props} />
        </TableContainerContext.Provider>
    );
}

export function Table(props: any) {
    const { className, children, sxProp, ...rest } = props;
    const insideContainer = useContext(TableContainerContext);
    const table = (
        <table className={cx('table table-zebra', className)} style={sxToStyle(sxProp)} {...rest}>
            {children}
        </table>
    );
    // 沒有放在 TableContainer 時也要有橫向捲動與固定表頭，否則窄螢幕會把表格擠出卡片
    return insideContainer ? table : <div className="data-table rounded-box">{table}</div>;
}

export function TableHead(props: any) {
    const { sxProp, ...rest } = props;
    return <thead style={sxToStyle(sxProp)} {...rest} />;
}

export function TableBody(props: any) {
    const { sxProp, ...rest } = props;
    return <tbody style={sxToStyle(sxProp)} {...rest} />;
}

export function TableRow(props: any) {
    const { hover, className, sxProp, selected, ...rest } = props;
    return (
        <tr
            className={cx(hover ? 'row-hover' : '', selected ? 'bg-primary/10' : '', className)}
            style={sxToStyle(sxProp)}
            {...rest}
        />
    );
}

export function TableCell(props: any) {
    const { align, className, component, sxProp, ...rest } = props;
    const Component = component || 'td';
    return (
        <Component
            className={cx(
                align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : '',
                className
            )}
            style={sxToStyle(sxProp)}
            {...rest}
        />
    );
}

export function TablePagination(props: any) {
    const {
        count,
        page,
        rowsPerPage,
        onPageChange,
        onRowsPerPageChange,
        rowsPerPageOptions = [10, 20, 50],
    } = props;
    const t = useSharedUiText();
    const totalPages = Math.max(Math.ceil(count / rowsPerPage), 1);
    return (
        <div className="flex flex-col gap-3 border-t border-base-300 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-base-content/60">
                {t('pagination.summary', { page: page + 1, totalPages, count })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <select
                    className="select select-sm w-auto"
                    aria-label={t('pagination.rowsPerPageLabel')}
                    value={rowsPerPage}
                    onChange={(event) => onRowsPerPageChange?.(event)}
                >
                    {rowsPerPageOptions.map((option: number) => (
                        <option key={option} value={option}>
                            {t('pagination.rowsPerPage', { count: option })}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={page <= 0}
                    onClick={() => onPageChange?.(null, Math.max(page - 1, 0))}
                >
                    {t('pagination.previous')}
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={page + 1 >= totalPages}
                    onClick={() => onPageChange?.(null, Math.min(page + 1, totalPages - 1))}
                >
                    {t('pagination.next')}
                </button>
            </div>
        </div>
    );
}

export function Tabs(props: any) {
    const {
        value,
        onChange,
        children,
        className,
        sxProp,
        variant,
        scrollButtons,
        allowScrollButtonsMobile,
        centered,
        orientation,
    } = props;
    void scrollButtons;
    void allowScrollButtonsMobile;
    void centered;
    const name = useId().replace(/:/g, '');
    const renderedChildren = Children.map(children, (child, index) => {
        if (!isValidElement(child)) return child;
        return cloneElement(child as any, { __tabIndexValue: index } as any);
    });

    return (
        <TabsContext.Provider
            value={{
                name,
                value,
                onChange: (_event, nextValue: unknown) => onChange?.(_event, nextValue),
            }}
        >
            <div
                className={cx(variant === 'scrollable' ? 'w-full overflow-x-auto' : '', className)}
                style={sxToStyle(sxProp)}
            >
                <div
                    role="tablist"
                    className={cx(
                        'tabs tabs-border',
                        variant === 'scrollable' ? 'tabs-nowrap' : 'flex-wrap',
                        orientation === 'vertical' ? 'tabs-vertical' : ''
                    )}
                >
                    {renderedChildren}
                </div>
            </div>
        </TabsContext.Provider>
    );
}

export function Tab(props: any) {
    const context = useContext(TabsContext);
    const {
        label,
        value,
        icon,
        iconPosition = 'start',
        className,
        children,
        sxProp,
        disabled,
        __tabIndexValue,
        ...rest
    } = props;
    const { domProps, style } = splitStyleProps(rest, sxProp);
    const content = label || children;
    const currentValue = value ?? __tabIndexValue ?? label;
    const active = context?.value === currentValue;

    if (!icon) {
        return (
            <input
                type="radio"
                role="tab"
                name={context?.name}
                className={cx('tab', active ? 'tab-active' : '', className)}
                aria-label={typeof content === 'string' ? content : String(currentValue)}
                checked={active}
                disabled={disabled}
                onChange={(event) => context?.onChange?.(event, currentValue)}
                style={style}
                {...domProps}
            />
        );
    }

    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            className={cx('tab gap-2', active ? 'tab-active' : '', className)}
            disabled={disabled}
            onClick={(event) => context?.onChange?.(event, currentValue)}
            style={style}
            {...domProps}
        >
            {iconPosition === 'end' ? null : icon}
            <span>{content}</span>
            {iconPosition === 'end' ? icon : null}
        </button>
    );
}

/** 標題與按鈕列固定在對話框上下緣，內容過長時只有中間捲動 */
function splitDialogChildren(children: ReactNode) {
    const body: ReactNode[] = [];
    let header: ReactNode = null;
    let actions: ReactNode = null;
    Children.toArray(children).forEach((child) => {
        if (isValidElement(child) && child.type === DialogTitle) {
            header = child;
        } else if (isValidElement(child) && child.type === DialogActions) {
            actions = child;
        } else {
            body.push(child);
        }
    });
    return { header, actions, body };
}

export function Dialog(props: any) {
    const { open, onClose, children, maxWidth = 'sm', fullWidth } = props;
    const titleId = useId();
    if (!open) return null;
    const { header, actions, body } = splitDialogChildren(children);
    return (
        <DialogTitleContext.Provider value={titleId}>
            <AppModal
                open={open}
                onClose={onClose}
                header={header}
                titleId={titleId}
                actions={actions}
                maxWidth={fullWidth ? resolveMaxWidth(maxWidth) : 'max-w-fit'}
            >
                {body}
            </AppModal>
        </DialogTitleContext.Provider>
    );
}

export function DialogTitle(props: any) {
    const titleId = useContext(DialogTitleContext);
    return (
        <h2
            id={titleId}
            className={cx('text-xl font-semibold', props.className)}
            style={sxToStyle(props.sxProp)}
        >
            {props.children}
        </h2>
    );
}

export function DialogContent(props: any) {
    return (
        <div className={cx('space-y-4', props.className)} style={sxToStyle(props.sxProp)}>
            {props.children}
        </div>
    );
}

export function DialogContentText(props: any) {
    return <p className={cx('text-sm text-base-content/70', props.className)}>{props.children}</p>;
}

export function DialogActions(props: any) {
    return (
        <DialogActionsContext.Provider value>
            <div
                className={cx('flex flex-wrap items-center justify-end gap-2', props.className)}
                style={sxToStyle(props.sxProp)}
            >
                {props.children}
            </div>
        </DialogActionsContext.Provider>
    );
}

export function Breadcrumbs(props: any) {
    const items = Children.toArray(props.children);
    return (
        <nav className={cx('text-sm breadcrumbs', props.className)} style={sxToStyle(props.sxProp)}>
            <ul>
                {items.map((child, index) => (
                    <li key={index}>{child}</li>
                ))}
            </ul>
        </nav>
    );
}

export function Accordion(props: any) {
    const { children, expanded, defaultExpanded, onChange, className, sxProp } = props;
    const [open, setOpen] = useState(defaultExpanded || expanded || false);
    useEffect(() => {
        if (expanded !== undefined) {
            setOpen(expanded);
        }
    }, [expanded]);

    return (
        <details
            className={cx('collapse-arrow collapse border border-base-300 bg-base-100', className)}
            open={open}
            style={sxToStyle(sxProp)}
            onToggle={(event) => {
                const next = (event.currentTarget as HTMLDetailsElement).open;
                setOpen(next);
                onChange?.(event, next);
            }}
        >
            {children}
        </details>
    );
}

export function AccordionSummary(props: any) {
    return (
        <summary
            className={cx('collapse-title min-h-0 p-4 text-base font-medium', props.className)}
        >
            <div className="flex items-center gap-2">
                {props.children}
                {props.expandIcon}
            </div>
        </summary>
    );
}

export function AccordionDetails(props: any) {
    return <div className={cx('collapse-content p-4 pt-0', props.className)}>{props.children}</div>;
}

export function ToggleButtonGroup(props: any) {
    const { value, onChange, exclusive, children, className, sxProp } = props;
    return (
        <ToggleContext.Provider value={{ value, exclusive, onChange }}>
            <div className={cx('join', className)} style={sxToStyle(sxProp)}>
                {children}
            </div>
        </ToggleContext.Provider>
    );
}

export function ToggleButton(props: any) {
    const context = useContext(ToggleContext);
    const { value, children, className, ...rest } = props;
    const selected = Array.isArray(context?.value)
        ? context?.value.includes(value)
        : context?.value === value;
    return (
        <button
            type="button"
            className={cx('btn join-item', selected ? 'btn-primary' : 'btn-outline', className)}
            onClick={(event) => context?.onChange?.(event, value)}
            {...rest}
        >
            {children}
        </button>
    );
}

export function Slider(props: any) {
    const { className, sxProp, ...rest } = props;
    return (
        <input
            type="range"
            className={cx('range range-primary', className)}
            style={sxToStyle(sxProp)}
            {...rest}
        />
    );
}

export function Stepper(props: any) {
    return (
        <StepperContext.Provider value={{ activeStep: props.activeStep || 0 }}>
            <ul className={cx('steps w-full', props.className)} style={sxToStyle(props.sxProp)}>
                {props.children}
            </ul>
        </StepperContext.Provider>
    );
}

export function Step(props: any) {
    const context = useContext(StepperContext);
    const indexRef = useRef<number | null>(null);
    const [index, setIndex] = useState(0);
    useEffect(() => {
        if (indexRef.current === null) {
            indexRef.current = 0;
        }
        setIndex(indexRef.current);
    }, []);
    return (
        <li className={cx('step', index <= context.activeStep ? 'step-primary' : '')}>
            {props.children}
        </li>
    );
}

export function StepLabel(props: any) {
    return <span>{props.children}</span>;
}

export function ImageList(props: any) {
    const cols = props.cols || 3;
    return (
        <div
            className={cx('grid', props.className)}
            style={{
                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                gap: `${props.gap || 8}px`,
                ...sxToStyle(props.sxProp),
            }}
        >
            {props.children}
        </div>
    );
}

export function ImageListItem(props: any) {
    return (
        <div className={cx('relative overflow-hidden rounded-2xl', props.className)}>
            {props.children}
        </div>
    );
}

export function ImageListItemBar(props: any) {
    return (
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-black/65 px-3 py-2 text-sm text-white">
            <span>{props.title}</span>
            {props.actionIcon}
        </div>
    );
}

export { Autocomplete } from './Autocomplete';

export function Navigate() {
    return null;
}

export { LoadingState, ErrorState, StatusBadge };
